/**
 * Activity section — the user's OWN recent account activity (self-scoped
 * audit trail: logins, profile edits, drafts, orders...). DPDP transparency
 * surface; the backend forces the actor filter to the signed-in user.
 *
 * Cursor-paginated ("Load more") against GET /auth/my-activity. Shows the
 * last ~90 days (older rows rotate into the audit archive, admin-only).
 */

import { useCallback, useEffect, useState } from 'react';
import { authAPI } from '../../services/api';
import { ProfileCard, Pill } from './ProfilePrimitives';
import Button from '../common/Button';

const MUTED = '#7a7060';
const TEXT = '#08111f';
const BORDER = '#eee6d8';

// "user.login_password_failed" → "Login password failed"
const humanizeAction = (action) => {
  const last = String(action || '').split('.').pop().replace(/_/g, ' ');
  return last.charAt(0).toUpperCase() + last.slice(1);
};

// Keys mirror AuditLog.deriveCategory (backend/models/AuditLog.js).
const CATEGORY_TONES = {
  auth: 'teal',
  company: 'navy',
  assignment: 'gold',
  draft: 'green',
  file: 'green',
  professional: 'navy',
  ai: 'teal',
  system: 'neutral',
};

const formatWhen = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
};

export default function ActivitySection() {
  const [rows, setRows] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (cursor) => {
    const res = await authAPI.getMyActivity({ cursor, limit: 30 });
    const data = res?.data || {};
    return { rows: data.rows || [], nextCursor: data.nextCursor || null };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const first = await load();
        if (cancelled) return;
        setRows(first.rows);
        setNextCursor(first.nextCursor);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Could not load your activity.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [load]);

  const loadMore = async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const page = await load(nextCursor);
      setRows((prev) => [...prev, ...page.rows]);
      setNextCursor(page.nextCursor);
    } catch (err) {
      setError(err?.message || 'Could not load more activity.');
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <ProfileCard
      title="Recent activity"
      subtitle="Actions performed by your account over the last ~90 days. Spot a sign-in you don't recognise? Change your password immediately."
      icon="audit"
    >
      {error && (
        <div className="mb-4 px-3 py-2 rounded-lg text-[12px]" style={{ background: '#f2e9e6', color: '#9a564a' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-[12.5px] py-8 text-center" style={{ color: MUTED }}>Loading your activity…</div>
      ) : rows.length === 0 ? (
        <div className="text-[12.5px] py-8 text-center" style={{ color: MUTED }}>No recorded activity yet.</div>
      ) : (
        <div>
          <ul className="m-0 p-0 list-none divide-y" style={{ borderColor: BORDER }}>
            {rows.map((row) => (
              <li key={row._id} className="py-3 flex items-start gap-3" style={{ borderColor: BORDER }}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[13px] font-medium" style={{ color: TEXT }}>
                      {humanizeAction(row.action)}
                    </span>
                    {row.actionCategory && (
                      <Pill tone={CATEGORY_TONES[row.actionCategory] || 'neutral'}>{row.actionCategory}</Pill>
                    )}
                    {row.success === false && <Pill tone="red">Failed</Pill>}
                  </div>
                  <div className="text-[11px] mt-[2px]" style={{ color: MUTED }}>
                    {row.targetName ? `${row.targetName} · ` : ''}
                    {formatWhen(row.createdAt)}
                    {row.ipAddress ? ` · from ${row.ipAddress}` : ''}
                  </div>
                </div>
              </li>
            ))}
          </ul>

          {nextCursor && (
            <div className="pt-4 border-t" style={{ borderColor: BORDER }}>
              <Button variant="outline" size="sm" onClick={loadMore} loading={loadingMore}>
                {loadingMore ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </div>
      )}
    </ProfileCard>
  );
}
